/**
 * `SourcingGateCard` — the honest disable-with-reason for the Sampling section.
 *
 * A sample cut before the real fabric arrives misrepresents hand-feel and
 * colour, so sampling is blocked until every non-process BOM line is `ready`
 * or `issued`. When the gate is closed, this card names the exact material
 * lines still waiting and links to the Materials tab. When it's open, the card
 * renders nothing — the Submit button in the parent takes over. On query
 * error, the card offers a retry so the operator isn't staring at a silently
 * dead Submit.
 *
 * The gate query is fetched by `SamplesSection` and passed in here — one
 * subscription, unambiguous loading/error states across the two consumers.
 *
 * @see packages/domain/src/garment/samples/get-sourcing-gate.ts
 * @see docs/adr/0047-block-sampling-until-sourced.md
 */
import { Link } from "@tanstack/react-router";
import type { inferRouterOutputs } from "@trpc/server";
import { AlertTriangle, ArrowRight, RefreshCw } from "lucide-react";
import type { AppRouter } from "@flint/api/router";
import { StatusPill } from "@flint/ui";
import { MATERIAL_PLAN_LABEL } from "@flint/domain/garment/materials/compute-material-plan";

export type SourcingGateData = inferRouterOutputs<AppRouter>["samples"]["sourcingGate"];

interface SourcingGateCardProps {
  styleId: string;
  data: SourcingGateData | undefined;
  isError: boolean;
  refetch: () => void;
}

export function SourcingGateCard({ styleId, data, isError, refetch }: SourcingGateCardProps) {
  if (isError) {
    return (
      <div className="mt-3.5 rounded-md border border-warn-100 bg-warn-50 px-4 py-3">
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warn" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-amber-800">
              Couldn't check sourcing status.
            </p>
            <p className="mt-1 text-xs text-amber-800">
              Submitting will still be verified server-side.
            </p>
            <button
              type="button"
              onClick={refetch}
              className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:underline"
            >
              <RefreshCw size={12} /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const gate = data;
  if (!gate || gate.status === "ready") return null;

  return (
    <div className="mt-3.5 rounded-md border border-warn-100 bg-warn-50 px-4 py-3">
      <div className="flex items-start gap-2.5">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warn" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-amber-800">
            Sourcing must be complete before sampling.
          </p>
          {gate.reason ? (
            <p className="mt-1 text-xs text-amber-800">{gate.reason}</p>
          ) : null}
          {gate.blockingLines.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {gate.blockingLines.map((line) => (
                <li key={line.bomLineId} className="flex items-center justify-between gap-3 text-xs">
                  <span className="text-ink-2">{line.label}</span>
                  <StatusPill tone="warn" size="xs">
                    {MATERIAL_PLAN_LABEL[line.plan]}
                  </StatusPill>
                </li>
              ))}
            </ul>
          ) : null}
          <Link
            to="/merchandising/styles/$styleId/materials"
            params={{ styleId }}
            className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:underline"
          >
            Go to Materials <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}
